/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';

import {
  Box,
  TextField,
  Grid,
  Button,
  Paper,
  Typography,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import PickerField from '../../../../components/PickerField';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';
import { useLanguage } from '../../../../utils/i18n';

import EditSquareIcon from '@mui/icons-material/EditSquare';
import DetailDialog from '../../../../components/DetailDialog';
import { getDriverLicenseDetailFile, useDriverLicenseList } from '../../../../stores/driverLicenseStore';
import { CloudDownload } from '@mui/icons-material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

import MasterDriverLicenseAddEdit from '../../driver-license/MasterDriverLicenseAddEdit';
import { readOnlyFieldStyle } from '../../../dashboard/fuel/constant';
import { SPACES } from '../../../../config/constants';

const formatDateShort = (val) => {
  if (!val) return '';
  try {
    const d = dayjs(val);
    if (!d.isValid()) return String(val).slice(0, 10);
    return d.format('DD-MM-YYYY');
  } catch {
    return String(val).slice(0, 10);
  }
};

const uniqueByValue = (arr = []) => {
  const map = new Map();
  (arr || []).forEach((item) => {
    const key = String(item.value ?? item.id ?? item.name ?? item.label ?? '');
    if (!map.has(key)) map.set(key, item);
  });
  return Array.from(map.values());
};

const checkLifetime = (val) => {
  return val === true || val === 1 || val === '1' || val === 'true';
};

const DriverLicenseForm = forwardRef(({ formik }, ref) => {
  const { values, setFieldValue } = formik;
  const { t } = useLanguage();
  const baseCurrent = {
    id: null,
    driver_license: '',
    license_type_label: '',
    license_no: '',
    license_exp_date: '',
    is_lifetime: false,
    upload_type: 'photo',
    front_photo: '',
    back_photo: '',
    document_name: '',
    document_file: null
  };

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailContent, setDetailContent] = useState({ type: 'photo', front: '', back: '', document: '', label: '' });

  const [current, setCurrent] = useState(baseCurrent);
  const [editingIndex, setEditingIndex] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmIndex, setConfirmIndex] = useState(null);
  const list = useMemo(() => (Array.isArray(values.driver_license) ? values.driver_license : []), [values.driver_license]);
  const [fileErrors, setFileErrors] = useState({ front: '', back: '', document: '' });
  const [fieldErrors, setFieldErrors] = useState({ license_type: false, license_no: false, license_exp_date: false });
  const MAX_FILE_SIZE = 2 * 1024 * 1024;

  const [searchLicense, setSearchLicense] = useState('');
  const [filterCompanyId, setFilterCompanyId] = useState('');

  useEffect(() => {
    const currentCompany = values.company ? String(values.company) : '';
    if (currentCompany !== filterCompanyId) {
      setFilterCompanyId(currentCompany);
    }
  }, [values.company]);

  const { data: driverLicenseData = [], isLoading: isLoadingLicense } = useDriverLicenseList(0, 100, '', searchLicense, filterCompanyId);

  const licenseOptions = useMemo(() => {
    const raw = (driverLicenseData || []).map((p) => ({
      value: p.id ?? String(p.id),
      label: p.license_name ?? p.name ?? `${p.id}`,
      is_lifetime: checkLifetime(p.is_lifetime),
      raw: p
    }));
    return uniqueByValue(raw);
  }, [driverLicenseData]);

  const handleCurrentChange = (key, val) => {
    setCurrent((c) => ({ ...c, [key]: val }));
    setFieldErrors((prev) => ({ ...prev, [key]: false }));
  };

  const handlePickerChange = (opt) => {
    const selected = (licenseOptions || []).find((o) => String(o.value ?? o.id) === String(opt)) ?? (typeof opt === 'object' ? opt : null);
    const id = selected ? (selected.value ?? selected.id) : (opt ?? null);
    const label = selected ? (selected.label ?? selected.name) : '';
    const isLifetime = checkLifetime(selected?.is_lifetime ?? selected?.raw?.is_lifetime);
    setCurrent((c) => ({
      ...c,
      driver_license: id,
      license_type_label: label,
      is_lifetime: isLifetime,
      license_exp_date: isLifetime ? '' : c.license_exp_date
    }));
    setFieldErrors((prev) => ({
      ...prev,
      license_type: false,
      ...(isLifetime ? { license_exp_date: false } : {})
    }));
  };

  const handleFileFront = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setCurrent((c) => ({
      ...c,
      front_photo_file: file,
      front_photo: URL.createObjectURL(file)
    }));
    if (file.size > MAX_FILE_SIZE) {
      setFileErrors((prev) => ({ ...prev, front: 'Front photo size must be less than 2MB' }));
      setCurrent((c) => ({ ...c, front_photo: '', front_photo_file: null }));
    } else {
      setFileErrors((prev) => ({ ...prev, front: '' }));
      setCurrent((c) => ({ ...c, front_photo_file: file, front_photo: URL.createObjectURL(file) }));
    }
  };

  const handleFileBack = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setCurrent((c) => ({
      ...c,
      back_photo_file: file,
      back_photo: URL.createObjectURL(file)
    }));
    if (file.size > MAX_FILE_SIZE) {
      setFileErrors((prev) => ({ ...prev, back: 'Back photo size must be less than 2MB' }));
      setCurrent((c) => ({ ...c, back_photo: '', back_photo_file: null }));
    } else {
      setFileErrors((prev) => ({ ...prev, back: '' }));
      setCurrent((c) => ({ ...c, back_photo_file: file, back_photo: URL.createObjectURL(file) }));
    }
  };

  const handleFileDoc = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setCurrent((c) => ({
      ...c,
      document_name: file.name,
      document_file: file,
      url_license: URL.createObjectURL(file)
    }));
    if (file.size > MAX_FILE_SIZE) {
      setFileErrors((prev) => ({ ...prev, document: 'Document size must be less than 2MB' }));
      setCurrent((c) => ({ ...c, document_name: file.name, document_file: null, url_license: '' }));
    } else {
      setFileErrors((prev) => ({ ...prev, document: '' }));
      setCurrent((c) => ({
        ...c,
        document_name: file.name,
        document_file: file,
        url_license: URL.createObjectURL(file)
      }));
    }
  };

  const handleAddCurrent = () => {
    let hasError = false;
    const errors = {
      license_type: false,
      license_no: false,
      license_exp_date: false
    };

    if (!current.driver_license) {
      errors.license_type = true;
      hasError = true;
    }
    if (!current.license_no || current.license_no.trim() === '') {
      errors.license_no = true;
      hasError = true;
    }
    if (!current.is_lifetime && !current.license_exp_date) {
      errors.license_exp_date = true;
      hasError = true;
    }

    if (hasError) {
      setFieldErrors(errors);
      return;
    }

    const copy = [...list];
    let itemToSave = {
      ...current,
      front_photo_file: current.front_photo_file,
      back_photo_file: current.back_photo_file,
      document_file: current.document_file
    };

    if (itemToSave.upload_type === 'photo') {
      itemToSave.url_license = '';
      itemToSave.document_file = null;
      itemToSave.document_name = '';
    } else if (itemToSave.upload_type === 'document') {
      itemToSave.front_photo = '';
      itemToSave.front_photo_file = null;
      itemToSave.back_photo = '';
      itemToSave.back_photo_file = null;
    }

    const finalItem = {
      ...itemToSave,
      is_lifetime: Boolean(current.is_lifetime),
      license_exp_date: current.is_lifetime ? '' : itemToSave.license_exp_date || '',
      is_completed: checkIsComplete({
        ...itemToSave,
        is_lifetime: Boolean(current.is_lifetime)
      })
    };

    if (editingIndex != null) {
      copy[editingIndex] = finalItem;
    } else {
      copy.push(finalItem);
    }

    setFieldValue('driver_license', copy);
    setCurrent(baseCurrent);
    setEditingIndex(null);
    setFileErrors({ front: '', back: '', document: '' });
    setFieldErrors({ license_type: false, license_no: false, license_exp_date: false });
  };

  const getOptionId = (o) => (o == null ? o : (o.value ?? o.id ?? o.license_id ?? o.license ?? o));
  const getOptionLabel = (o) =>
    o == null ? '' : (o.label ?? o.name ?? o.license_name ?? o.license_label ?? String(o.value ?? o.id ?? ''));

  const optionMatches = (opt, value) => {
    if (!opt) return false;
    const optIds = [
      opt.value ?? undefined,
      opt.id ?? undefined,
      opt.license_id ?? undefined,
      opt.license ?? undefined,
      opt.code ?? undefined
    ].map((v) => (v == null ? '' : String(v)));
    const val = value == null ? '' : String(value);
    return optIds.includes(val);
  };

  const findOptionByValue = (options = [], value) => {
    if (value == null || value === '') return null;
    return (
      (options || []).find((o) => {
        try {
          return optionMatches(o, value);
        } catch {
          return false;
        }
      }) ?? null
    );
  };

  const findOptionByLabel = (options = [], label) => {
    if (!label) return null;
    return (
      (options || []).find((o) => {
        try {
          const lab = (getOptionLabel(o) || '').toLowerCase();
          return lab === String(label).toLowerCase();
        } catch {
          return false;
        }
      }) ?? null
    );
  };

  useEffect(() => {
    if (!Array.isArray(values.driver_license) || !licenseOptions || licenseOptions.length === 0) return;
    let changed = false;
    const normalized = values.driver_license.map((it) => {
      const copy = { ...it };
      const found =
        findOptionByValue(licenseOptions, copy.driver_license) ||
        findOptionByLabel(licenseOptions, copy.license_type_label || copy.license_name);

      if (found) {
        if (copy.driver_license == null || copy.driver_license === '') {
          copy.driver_license = getOptionId(found);
          changed = true;
        }
        if (!copy.license_type_label) {
          copy.license_type_label = getOptionLabel(found);
          changed = true;
        }
        if (copy.is_lifetime === undefined && found.is_lifetime !== undefined) {
          copy.is_lifetime = checkLifetime(found.is_lifetime);
          changed = true;
        }
      }
      if (copy.data_front_photo == null) copy.data_front_photo = {};
      if (copy.data_back_photo == null) copy.data_back_photo = {};
      if (!Object.prototype.hasOwnProperty.call(copy, 'photo')) copy.photo = null;
      return copy;
    });
    if (changed) setFieldValue('driver_license', normalized);
  }, [licenseOptions, values.driver_license]);

  useEffect(() => {
    if (!Array.isArray(values.driver_license_removed)) {
      setFieldValue('driver_license_removed', []);
    }
  }, []);

  const extractId = (val) => {
    if (val == null) return '';
    if (typeof val === 'object') return String(val.value ?? val.id ?? val.driver_license ?? val.license_id ?? '');
    return String(val);
  };

  const usedTypes = useMemo(() => {
    const ids = new Set();
    const labels = new Set();

    (list || []).forEach((it, idx) => {
      if (editingIndex != null && idx === editingIndex) return;

      const rawId = it.driver_license ?? it.license_type ?? it.license_id;
      const idStr = extractId(rawId);
      if (idStr) ids.add(idStr);

      const rawLabel = it.license_type_label ?? it.license_name ?? it.join?.driver_license_license_name ?? '';
      if (rawLabel) {
        labels.add(String(rawLabel).trim().toLowerCase());
        const matched = findOptionByLabel(licenseOptions, rawLabel);
        if (matched) {
          ids.add(String(getOptionId(matched)));
        }
      }

      if (idStr) {
        const matched = findOptionByValue(licenseOptions, idStr);
        if (matched) {
          labels.add(String(getOptionLabel(matched)).trim().toLowerCase());
        }
      }
    });

    return { ids, labels };
  }, [list, editingIndex, licenseOptions]);

  const availableLicenseOptions = useMemo(() => {
    return (licenseOptions || []).filter((opt) => {
      const id = String(getOptionId(opt) ?? '');
      const label = String(getOptionLabel(opt) ?? '')
        .trim()
        .toLowerCase();

      if (editingIndex != null) {
        const editingItem = list[editingIndex];
        const editingId = extractId(editingItem?.driver_license ?? editingItem?.license_type ?? editingItem?.license_id);
        const editingLabel = String(
          editingItem?.license_type_label ?? editingItem?.license_name ?? editingItem?.join?.driver_license_license_name ?? ''
        )
          .trim()
          .toLowerCase();

        if ((editingId && editingId === id) || (editingLabel && editingLabel === label)) {
          return true;
        }
      }

      if (id && usedTypes.ids.has(id)) return false;
      if (label && usedTypes.labels.has(label)) return false;

      return true;
    });
  }, [licenseOptions, usedTypes, editingIndex, list]);

  const handleRemoveLicenseConfirm = (idx) => {
    setConfirmIndex(idx);
    setConfirmOpen(true);
  };

  const handleConfirmRemove = () => {
    const idx = confirmIndex;
    const copy = Array.isArray(values.driver_license) ? [...values.driver_license] : [];
    if (idx >= 0 && idx < copy.length) {
      const removedItem = copy[idx];
      if (removedItem?.id) {
        const currentRemoved = Array.isArray(values.driver_license_removed) ? [...values.driver_license_removed] : [];
        const nextRemoved = Array.from(new Set([...currentRemoved, removedItem.id]));
        setFieldValue('driver_license_removed', nextRemoved);
        const filteredRemovals = (values._images_to_delete || []).filter((d) => d.id !== removedItem.id);
        setFieldValue('_images_to_delete', filteredRemovals);
      }
      copy.splice(idx, 1);
      setFieldValue('driver_license', copy);
    }
    setConfirmOpen(false);
    setConfirmIndex(null);
  };

  const handleCancelRemove = () => {
    setConfirmOpen(false);
    setConfirmIndex(null);
  };

  const [removeFiles, setRemoveFiles] = useState([]);

  const getFileNameFromUrl = (url) => {
    if (!url || url.startsWith('blob:')) return '';
    try {
      const cleanUrl = url.split('?')[0];
      const fileName = cleanUrl.split('/').pop();
      return fileName;
    } catch {
      return '';
    }
  };

  const handleRemoveFileField = (fieldName, type) => {
    const currentUrl = current[fieldName];
    const fileName = getFileNameFromUrl(currentUrl);
    if (current.id && fileName) {
      const existingRemovals = Array.isArray(values._images_to_delete) ? values._images_to_delete : [];
      const alreadyQueued = existingRemovals.some((item) => item.id === current.id && item.type === type && item.fileName === fileName);
      if (!alreadyQueued) {
        setFieldValue('_images_to_delete', [...existingRemovals, { id: current.id, type: type, fileName: fileName }]);
      }
    }
    setCurrent((prev) => ({
      ...prev,
      [fieldName]: '',
      [`${fieldName}_file`]: null,
      ...(type === 'document' ? { document_file: null, document_name: '' } : {})
    }));
  };

  useImperativeHandle(ref, () => ({
    getFiles: () => list,
    getRemoveFiles: () => removeFiles,
    getRemovedLicenseIds: () => {
      return Array.isArray(formik.values.driver_license_removed) ? formik.values.driver_license_removed : [];
    },
    clearRemoveFiles: () => setRemoveFiles([])
  }));

  const getImageUrl = (item) => {
    if (item.front_photo && item.front_photo.startsWith('blob:') && !item.id) {
      return item.front_photo;
    }
    if (item.id) {
      const token = localStorage.getItem('apiKey');
      const driverId = item.driver || values.id;
      return `${SPACES.FMS_ITI_SPACES}/oslog/prod/images/driver-license/${driverId}/${item.id}_${driverId}_front.jpeg?apikey=${token}`;
    }
    return null;
  };

  const handleViewLicense = async (idx) => {
    const item = list[idx];
    let updatedItem = { ...item };
    const foundLicense =
      findOptionByValue(licenseOptions, item.driver_license) ||
      findOptionByLabel(licenseOptions, item.license_type_label || item.license_name);
    const isLifetime = checkLifetime(item.is_lifetime ?? foundLicense?.is_lifetime ?? foundLicense?.raw?.is_lifetime);
    updatedItem.is_lifetime = isLifetime;
    if (isLifetime) {
      updatedItem.license_exp_date = '';
    }

    if (item.id) {
      try {
        const [frontRes, backRes, docRes] = await Promise.allSettled([
          getDriverLicenseDetailFile(item.id, 'front'),
          getDriverLicenseDetailFile(item.id, 'back'),
          getDriverLicenseDetailFile(item.id, 'document')
        ]);

        updatedItem.front_photo = frontRes.status === 'fulfilled' ? frontRes.value : '';
        updatedItem.back_photo = backRes.status === 'fulfilled' ? backRes.value : '';
        updatedItem.url_license = docRes.status === 'fulfilled' ? docRes.value : '';

        if (updatedItem.front_photo || updatedItem.back_photo) {
          updatedItem.upload_type = 'photo';
        } else if (updatedItem.url_license) {
          updatedItem.upload_type = 'document';
          updatedItem.document_name = getFileNameFromUrl(updatedItem.url_license) || 'License Document';
        } else {
          updatedItem.upload_type = 'photo';
        }
      } catch (err) {
        console.error('Failed to fetch images', err);
      }
    }

    setCurrent(updatedItem);
    setEditingIndex(idx);
    setFieldErrors({ license_type: false, license_no: false, license_exp_date: false });
  };

  const handleOpenDetail = async (item) => {
    const label = item.license_name || item.license_type_label || 'License Detail';
    setDetailOpen(true);
    setDetailContent((prev) => ({ ...prev, label }));

    if (item.id) {
      try {
        const [frontRes, backRes, docRes] = await Promise.allSettled([
          getDriverLicenseDetailFile(item.id, 'front'),
          getDriverLicenseDetailFile(item.id, 'back'),
          getDriverLicenseDetailFile(item.id, 'document')
        ]);

        const front = frontRes.status === 'fulfilled' ? frontRes.value : '';
        const back = backRes.status === 'fulfilled' ? backRes.value : '';
        const doc = docRes.status === 'fulfilled' ? docRes.value : '';

        setDetailContent({
          label,
          front,
          back,
          document: doc,
          type: front || back ? 'photo' : doc ? 'document' : 'photo'
        });
      } catch (err) {
        console.error('Preview failed', err);
      }
    } else {
      setDetailContent({
        label,
        front: item.front_photo || '',
        back: item.back_photo || '',
        document: item.document_file ? URL.createObjectURL(item.document_file) : '',
        type: item.upload_type
      });
    }
  };

  const [serverFileStatus, setServerFileStatus] = useState({});
  const [isChecking, setIsChecking] = useState(true);

  const checkFilesOnServer = async () => {
    setIsChecking(true);
    const statusMap = {};
    const itemsToCheck = list.filter((item) => item.id);

    await Promise.all(
      itemsToCheck.map(async (item) => {
        try {
          let res = null;
          if (item.upload_type === 'document') {
            res = await getDriverLicenseDetailFile(item.id, 'document');
          } else if (item.upload_type === 'photo') {
            res = await getDriverLicenseDetailFile(item.id, 'front');
          } else {
            const [front, doc] = await Promise.all([
              getDriverLicenseDetailFile(item.id, 'front').catch(() => null),
              getDriverLicenseDetailFile(item.id, 'document').catch(() => null)
            ]);
            res = front || doc;
          }

          statusMap[item.id] = !!(res && res.length > 10);
        } catch (err) {
          console.log(err);
          statusMap[item.id] = false;
        }
      })
    );

    setServerFileStatus(statusMap);
    setIsChecking(false);
  };

  useEffect(() => {
    if (list.length > 0) {
      checkFilesOnServer();
    }
  }, [list.length]);

  const checkIsComplete = (item) => {
    const isLife = checkLifetime(
      item.is_lifetime ??
        findOptionByValue(licenseOptions, item.driver_license)?.is_lifetime ??
        findOptionByLabel(licenseOptions, item.license_type_label || item.license_name)?.is_lifetime
    );
    const hasNo = !!item.license_no && item.license_no.trim() !== '';
    const hasExp = isLife ? true : !!item.license_exp_date && !item.license_exp_date.startsWith('1900');
    let hasFile = false;
    if (item.id) {
      if (isChecking && serverFileStatus[item.id] === undefined) {
        hasFile = true;
      } else {
        const existsOnServer = serverFileStatus[item.id] === true;
        const isDeleted = values._images_to_delete?.some((d) => d.id === item.id);
        const hasReplacementFile = !!(item.front_photo_file || item.document_file);
        hasFile = (existsOnServer && !isDeleted) || hasReplacementFile;
      }
    } else {
      hasFile = !!(item.front_photo_file || item.document_file);
    }

    return hasNo && hasExp && hasFile;
  };

  const getIframeSrc = () => {
    const url = current.url_license;
    if (!url) return null;
    if (url.startsWith('blob:')) return url;
    if (url.includes('localhost') || url.includes('127.0.0.1')) return url;
    return `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container spacing={3} xs={12} md={12}>
        <Grid xs={12} md={12} sx={{ width: '48%' }}>
          <Grid item xs={12} md={12} mt={2}>
            <PickerField
              label={t('masterVehicle.licenseType', 'License Type') + ' *'}
              value={current.driver_license ?? ''}
              options={availableLicenseOptions}
              listOptions={availableLicenseOptions}
              onSearch={(text) => setSearchLicense(text)}
              isLoading={isLoadingLicense}
              onChange={(opt) => handlePickerChange(opt)}
              allowAdd
              AddComponent={MasterDriverLicenseAddEdit}
              getOptionId={(o) => getOptionId(o)}
              getOptionLabel={(o) => getOptionLabel(o)}
              dialogTitle="Select License Type"
              style={readOnlyFieldStyle}
              error={fieldErrors.license_type}
              helperText={
                !values.company
                  ? t('general.selectCompanyFirst', '* Please select a company first')
                  : fieldErrors.license_type
                    ? 'License Type is required'
                    : ''
              }
              disabled={!values.company}
            />
          </Grid>
          <Grid xs={12} md={12} mt={2}>
            <TextField
              label={t('masterVehicle.licenseNumber', 'License Number') + ' *'}
              size="medium"
              fullWidth
              name="license_no"
              value={current.license_no ?? ''}
              onChange={(e) => handleCurrentChange('license_no', e.target.value)}
              sx={readOnlyFieldStyle}
              error={fieldErrors.license_no}
              helperText={fieldErrors.license_no ? 'License Number is required' : ''}
              InputLabelProps={{
                sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
              }}
            />
          </Grid>
          <Grid xs={12} md={12} mt={2}>
            <DatePicker
              format="DD-MM-YYYY"
              label={current.is_lifetime ? t('general.expiredDate', 'Expired Date') : t('general.expiredDate', 'Expired Date') + ' *'}
              disabled={Boolean(current.is_lifetime)}
              value={current.license_exp_date && !current.is_lifetime ? dayjs(current.license_exp_date) : null}
              onChange={(newVal) => {
                handleCurrentChange('license_exp_date', newVal && newVal.isValid() ? newVal.format('YYYY-MM-DD') : '');
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  disabled: Boolean(current.is_lifetime),
                  error: !current.is_lifetime && fieldErrors.license_exp_date,
                  helperText: !current.is_lifetime && fieldErrors.license_exp_date ? 'Expired Date is required' : '',
                  InputLabelProps: {
                    sx: current.is_lifetime ? {} : { '& .MuiFormLabel-asterisk': { color: 'error.main' } }
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} mt={2}>
            <ToggleButtonGroup
              value={current.upload_type}
              exclusive
              onChange={(e, val) => val && handleCurrentChange('upload_type', val)}
              fullWidth
              size="small"
            >
              <ToggleButton value="photo">
                <PhotoLibraryIcon sx={{ mr: 1 }} /> {t('masterDriver.photoFrontBack', 'Photo (Front/Back)')}
              </ToggleButton>
              <ToggleButton value="document">
                <DescriptionIcon sx={{ mr: 1 }} /> {t('masterDriver.documentPdfImage', 'Document (PDF/Image)')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          {current.upload_type === 'photo' ? (
            <Grid item xs={12} mt={2}>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  <Paper
                    variant="outlined"
                    sx={{ height: 180, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                  >
                    {current.front_photo ? (
                      <img src={current.front_photo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <InsertPhotoOutlinedIcon color="disabled" />
                    )}
                  </Paper>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" component="label" size="small" fullWidth sx={{ textTransform: 'none' }}>
                      {t('masterDriver.uploadFront', 'Upload Front')}
                      <input type="file" hidden accept="image/*" onChange={handleFileFront} />
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      color="error"
                      onClick={() => handleRemoveFileField('front_photo', 'front')}
                    >
                      {t('buttonTitle.remove', 'Remove')}
                    </Button>
                  </Box>
                  {fileErrors.front && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {fileErrors.front}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  <Paper
                    variant="outlined"
                    sx={{ height: 180, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                  >
                    {current.back_photo ? (
                      <img src={current.back_photo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <InsertPhotoOutlinedIcon color="disabled" />
                    )}
                  </Paper>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" component="label" size="small" fullWidth>
                      {t('masterDriver.uploadBack', 'Upload Back')}
                      <input type="file" hidden accept="image/*" onChange={handleFileBack} />
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      color="error"
                      onClick={() => handleRemoveFileField('back_photo', 'back')}
                    >
                      {t('buttonTitle.remove', 'Remove')}
                    </Button>
                  </Box>
                  {fileErrors.back && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {fileErrors.back}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Grid>
          ) : (
            <Grid item xs={12} mt={2}>
              <Box sx={{ p: 2, border: '1px dashed grey', textAlign: 'center', borderRadius: 1 }}>
                {(current.url_license || current.document_file) && (
                  <Box sx={{ mb: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {getIframeSrc() && (
                      <iframe
                        title="Document Preview"
                        src={getIframeSrc()}
                        width="100%"
                        height="300px"
                        style={{ border: 'none', borderRadius: '4px', backgroundColor: '#f5f5f5' }}
                      />
                    )}
                  </Box>
                )}
                <input id="doc-upload" type="file" hidden onChange={handleFileDoc} />
                <label htmlFor="doc-upload">
                  <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} sx={{ textTransform: 'none' }}>
                    {current.document_name || current.url_license
                      ? t('masterDriver.changeDocument', 'Change Document')
                      : t('masterDriver.chooseDocument', 'Choose Document')}
                  </Button>
                </label>
                {(current.document_name || current.url_license) && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    sx={{ textTransform: 'none', ml: 1, verticalAlign: 'top' }}
                    onClick={() => handleRemoveFileField('url_license', 'document')}
                  >
                    {t('buttonTitle.remove', 'Remove')}
                  </Button>
                )}
                <Typography variant="body2" sx={{ mt: 1, color: fileErrors.document ? 'error.main' : 'text.primary' }}>
                  {current.document_name || 'No file selected'}
                </Typography>

                {fileErrors.document && (
                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                    {fileErrors.document}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
          <Grid item xs={12} mt={2}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="medium"
                onClick={() => {
                  if (current.id) {
                    const filteredRemovals = (values._images_to_delete || []).filter((d) => d.id !== current.id);
                    setFieldValue('_images_to_delete', filteredRemovals);
                  }
                  setCurrent(baseCurrent);
                  setEditingIndex(null);
                  setFileErrors({ front: '', back: '', document: '' });
                  setFieldErrors({ license_type: false, license_no: false, license_exp_date: false });
                }}
              >
                {t('buttonTitle.cancel', 'Cancel')}
              </Button>
              <Button
                size="medium"
                variant="contained"
                onClick={handleAddCurrent}
                disabled={!current.driver_license || !!fileErrors.front || !!fileErrors.back || !!fileErrors.document}
              >
                {editingIndex !== null ? t('buttonTitle.update', 'Update License') : t('buttonTitle.addToList', 'Add to List')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
        <Grid xs={12} md={12} sx={{ width: '48%' }}>
          <Grid xs={12} md={12} mt={2}>
            {!list || list.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <InsertPhotoOutlinedIcon sx={{ fontSize: 64, color: 'grey.300' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('masterVehicle.noLicense', 'No License')}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {list.map((item = {}, idx) => {
                  const opt = findOptionByValue(licenseOptions, item.driver_license);
                  const isLife = checkLifetime(item.is_lifetime ?? opt?.is_lifetime ?? opt?.raw?.is_lifetime);
                  const label =
                    item.join?.driver_license_license_name ||
                    item.license_type_label ||
                    item.license_name ||
                    (opt ? getOptionLabel(opt) : '') ||
                    t('general.unknown', 'Unknown Type');
                  const expFormatted = isLife
                    ? t('driverLicense.lifetime', 'Lifetime')
                    : item.license_exp_date
                      ? formatDateShort(item.license_exp_date)
                      : null;
                  const isActuallyComplete = checkIsComplete(item);
                  return (
                    <Paper
                      key={`list-${idx}`}
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                    >
                      <Box sx={{ width: 84, height: 64, borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                        <Avatar
                          variant="rounded"
                          src={item.upload_type === 'document' || item.url_license ? null : getImageUrl(item)}
                          sx={{
                            width: '100%',
                            height: '100%',
                            bgcolor: 'grey.100',
                            cursor: 'pointer',
                            p: item.upload_type === 'document' || item.url_license ? 1 : 0
                          }}
                          onClick={() => handleOpenDetail(item)}
                        >
                          {item.upload_type === 'document' || item.url_license ? (
                            <InsertDriveFileIcon sx={{ fontSize: 32, color: '#004aad' }} />
                          ) : (
                            <InsertDriveFileIcon sx={{ fontSize: 32, color: '#004aad' }} />
                          )}
                        </Avatar>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                          No: {item.license_no || '—'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <Chip
                            label={isLife ? t('driverLicense.lifetime', 'Lifetime') : expFormatted ? `Exp: ${expFormatted}` : 'No Exp'}
                            size="small"
                          />
                        </Stack>
                      </Box>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={label} size="medium" variant="contained" color={isActuallyComplete ? 'info' : 'warning'} />
                        <Tooltip title="View / Edit">
                          <IconButton size="small" onClick={() => handleViewLicense(idx)}>
                            <EditSquareIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => handleRemoveLicenseConfirm(idx)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Grid>
      <DetailDialog open={detailOpen} onClose={() => setDetailOpen(false)} title={detailContent.label} maxWidth="sm">
        <Box>
          {detailContent.type === 'photo' ? (
            <Grid container spacing={3} justifyContent="center" alignItems="flex-start">
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: 'text.secondary' }}>
                  FRONT PHOTO
                </Typography>
                <Box
                  sx={{
                    height: 180,
                    maxWidth: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#0000',
                    overflow: 'hidden'
                  }}
                >
                  {detailContent.front ? (
                    <Box
                      component="img"
                      src={detailContent.front}
                      alt="Front"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Stack alignItems="center" sx={{ minWidth: 300 }} spacing={1}>
                      <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                      <Typography variant="body2" color="text.disabled">
                        No Image
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: 'text.secondary' }}>
                  BACK PHOTO
                </Typography>
                <Box
                  sx={{
                    height: 180,
                    maxWidth: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#0000',
                    overflow: 'hidden'
                  }}
                >
                  {detailContent.back ? (
                    <Box component="img" src={detailContent.back} alt="Back" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Stack alignItems="center" sx={{ minWidth: 300 }} spacing={1}>
                      <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                      <Typography variant="body2" color="text.disabled">
                        No Image
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Paper
                variant="outlined"
                sx={{ height: 250, borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
              >
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <iframe
                    width="100%"
                    height="250px"
                    src={`https://docs.google.com/gview?url=${detailContent.document}&embedded=true`}
                    style={{ marginBottom: '8px' }}
                  />
                </Box>

                <Paper
                  variant="outlined"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    width: 250,
                    margin: '0 auto',
                    backgroundColor: '#f8f9fa',
                    borderStyle: 'dashed',
                    borderColor: '#1976d2',
                    p: 2
                  }}
                >
                  <Tooltip
                    title={
                      detailContent.document
                        ? decodeURIComponent(detailContent.document.split('?')[0].split('/').pop())
                        : 'Document_Driver.pdf'
                    }
                    arrow
                  >
                    <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ maxWidth: 300 }}>
                      {detailContent.document
                        ? decodeURIComponent(detailContent.document.split('?')[0].split('/').pop())
                        : 'Document_Driver.pdf'}
                    </Typography>
                  </Tooltip>
                  <Button type="button" startIcon={<CloudDownload />} component="label" size="small" variant="contained">
                    Download File
                  </Button>
                </Paper>
              </Paper>
            </Box>
          )}
        </Box>
      </DetailDialog>

      <DeleteConfirmDialog
        open={confirmOpen}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        itemName={confirmIndex != null && list[confirmIndex] ? list[confirmIndex].license_name || list[confirmIndex].license_no : ''}
        loading={false}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        confirmLabel={t('buttonTitle.remove', 'Remove')}
        cancelLabel={t('buttonTitle.cancel', 'Cancel')}
      />
    </LocalizationProvider>
  );
});

export default DriverLicenseForm;
